import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories } from '@/mocks/articles';

const ITEMS_PER_PAGE = 9;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  const currentCategory = categories.find((c) => c.slug === slug);
  const isAll = slug === 'all';

  const filteredArticles = isAll
    ? articles
    : articles.filter((a) => a.category === slug);

  const totalPages = Math.ceil(filteredArticles.length / ITEMS_PER_PAGE);
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [slug]);

  if (!currentCategory && !isAll) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-950">
          <Navbar />
          <main className="pt-20 w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto py-20 text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-background-800">
                <i className="ri-emotion-sad-line text-3xl text-foreground-500"></i>
              </div>
              <h1 className="text-2xl font-bold text-foreground-200 mb-2">分类不存在</h1>
              <p className="text-sm text-foreground-500 mb-6">您访问的分类页面不存在或已被移除</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const categoryName = isAll ? '全部资讯' : currentCategory?.name || '';

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <main className="pt-20 w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: categoryName },
              ]}
            />

            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-100 mb-2">
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-500">
                共 {filteredArticles.length} 篇相关资讯
              </p>
            </div>

            {/* Category Quick Nav */}
            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide">
              <Link
                to="/category/all"
                className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all cursor-pointer ${
                  isAll
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-800 text-foreground-400 hover:text-foreground-200 border border-background-700/50'
                }`}
              >
                全部
              </Link>
              {categories.filter((c) => c.id !== 'all').map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-800 text-foreground-400 hover:text-foreground-200 border border-background-700/50'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Articles Grid */}
            {paginatedArticles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {paginatedArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10 pb-14">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-800 text-foreground-400 hover:text-foreground-200 disabled:opacity-40 disabled:cursor-not-allowed border border-background-700/50 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-all cursor-pointer ${
                          page === currentPage
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-800 text-foreground-400 hover:text-foreground-200 border border-background-700/50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-800 text-foreground-400 hover:text-foreground-200 disabled:opacity-40 disabled:cursor-not-allowed border border-background-700/50 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="py-20 text-center">
                <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-background-800">
                  <i className="ri-inbox-line text-3xl text-foreground-500"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无内容</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}